import React, { useCallback } from 'react';
import { Linking, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';
import { Button, HStack, theme, useResponsive } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { useOrderStatusNavigation } from '../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const OrderStatusCtas = (props: OrderStatusCtasProps) => {
  const {
    orderId = '',
    orderTotal = 0,
    isPendingFeedback,
    momoSurveyUrl,
    canReorder,
    onReorder,
  } = props;

  const { t, formatPrice } = useLocalizationContext();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();
  const { match } = useResponsive();
  const { track } = useTelemetry();

  const { navigateToRateOrderScreen } = useOrderStatusNavigation();
  const orderTotalPrice = formatPrice(orderTotal, 'USD');

  // ─── Helpers ─────────────────────────────────────────────────────

  const handleRateOrder = useCallback(() => {
    if (!orderId) return;

    if (momoSurveyUrl) {
      track('order_status.in_store_rate_order_cta.press');
      Linking.openURL(momoSurveyUrl);
      return;
    }

    navigateToRateOrderScreen(orderId);
  }, [orderId, navigateToRateOrderScreen, track, momoSurveyUrl]);

  // ─── Layout ──────────────────────────────────────────────────────

  const stackStyle = match([undefined, styles.stackSM]);
  const hasTwoButtons = isPendingFeedback && canReorder;
  const buttonsStackItemsPerRow = match([hasTwoButtons ? 2 : 1, undefined]);
  const buttonsSize = match(['large', 'medium'] as const);

  // ─── Styles ──────────────────────────────────────────────────────

  const containerStyle = useStyle(
    () => ({ paddingBottom: safeAreaBottom }),
    [safeAreaBottom],
  );

  // ─────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyle}>
      <HStack
        itemsPerRow={buttonsStackItemsPerRow}
        gap={theme.spacing['4']}
        style={stackStyle}
      >
        {isPendingFeedback ? (
          <Button
            testID="rate-order-button"
            palette="secondary"
            size={buttonsSize}
            accessibilityLabel={t('order-status-info.rate-order')}
            accessibilityHint={t('order-status-info.rate-order')}
            onPress={handleRateOrder}
          >
            {t('order-status-info.rate-order')}
          </Button>
        ) : null}

        {canReorder ? (
          <Button
            testID="reorder-button"
            palette="primary"
            size={buttonsSize}
            accessibilityLabel={t('order-status-info.reorder')}
            accessibilityHint={t('order-status-info.reorder')}
            onPress={onReorder}
          >
            {isPendingFeedback
              ? t('order-status-info.reorder')
              : t('order-status-info.reorder-total', {
                  total: orderTotalPrice,
                })}
          </Button>
        ) : null}
      </HStack>
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stackSM: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-end',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────────

type OrderStatusCtasProps = Readonly<{
  orderId?: string;
  orderTotal?: number;
  isPendingFeedback: boolean;
  momoSurveyUrl?: string | null;
  canReorder: boolean;
  onReorder: () => void;
}>;
